<template>
  <div class="accessorials-edit flex flex-column flex-gap-1 align-items-start">
    <p v-if="!internalValue.length">
      {{ t('common.noParam', { param: t('referenceData.accessorials', 2) }) }}
    </p>
    <AccessorialEdit
      v-for="(accessorial, i) of internalValue"
      :key="i"
      :model-value="accessorial"
      :accessorial-codes="filteredAccessorialCodes"
      :disabled="disabled"
      class="align-self-stretch"
      @remove="onRemove(i)"
      @update:model-value="onUpdate(i, $event)"
    />
    <AddButton
      :disabled="disabled"
      :label="t('referenceData.accessorials')"
      class="w-auto"
      @click="onAdd"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useValidations } from '@rygen/rygen-ui/composables';
import { AccessorialCode } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { useAccessorialCodeStore } from '@/stores/AccessorialCode';
import { useParentEntityStore } from '@/stores/ParentEntity';

import AccessorialEdit from '@/components/accessorial/AccessorialEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';

const props = defineProps({
  modelValue: {
    type: Array as PropType<AccessorialCode[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', modelValue: AccessorialCode[]): void;
}>();

const { accessorialCodes, loadRequestableAccessorialCodes } =
  useAccessorialCodeStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod(opts: Partial<AccessorialCode[]>) {
    return opts.filter(notNull);
  },
});
const { owningClient } = useParentEntityStore();
useValidations(internalValue, {});

const filteredAccessorialCodes = computed(() => {
  return (
    accessorialCodes.value.data?.filter(
      (code) => !internalValue.value.some((item) => item.id === code.id),
    ) ?? []
  );
});

const onAdd = () => {
  internalValue.value.push(new AccessorialCode());
};

const onRemove = (i: number) => {
  internalValue.value.splice(i, 1);
};

const onUpdate = (index: number, accessorial: AccessorialCode) => {
  internalValue.value.splice(index, 1, accessorial);
};

onMounted(() => {
  if (!disabled.value && owningClient.value) {
    loadRequestableAccessorialCodes();
  }
});
</script>
